import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      navigate("/");
      return;
    }
    
    checkPaymentStatus();
  }, [sessionId, navigate]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 5;
    
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setPaymentStatus("success");
        return;
      } else if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(attempts + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setPaymentStatus("pending");
      }
    } catch (error) {
      console.error("Payment status check error:", error);
      if (attempts < maxAttempts) {
        setTimeout(() => {
          setAttempts(attempts + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setPaymentStatus("error");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {paymentStatus === "checking" && (
            <>
              <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Processing Payment...
              </h2>
              <p className="text-gray-600">
                Please wait while we confirm your payment.
              </p>
            </>
          )}

          {paymentStatus === "success" && (
            <>
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Successful!
              </h2>
              <p className="text-gray-600 mb-6">
                Thank you for your booking! We've received your payment and sent a confirmation email to your inbox.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white"
              >
                Return to Home
              </Button>
            </>
          )}

          {paymentStatus === "pending" && (
            <>
              <div className="bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="h-10 w-10 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Processing
              </h2>
              <p className="text-gray-600 mb-6">
                Your payment is being processed. You'll receive a confirmation email shortly.
              </p>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
              >
                Return to Home
              </Button>
            </>
          )}

          {paymentStatus === "error" && (
            <>
              <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚠️</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Status Unknown
              </h2>
              <p className="text-gray-600 mb-6">
                We couldn't verify your payment status. Please check your email for confirmation or contact us.
              </p>
              <Button
                onClick={() => navigate("/")}
                variant="outline"
              >
                Return to Home
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
